import storage, { storagekeys } from '@/utils/storage'
import type { CompanyListItem, CreditInfo, LoginRepsData, WalletInfo } from '@/servers/user/types'
import { getStores } from '@/stores'
import { defaultSelectedCompanyInfo, defaultUserInfo } from '@/stores/user'
import { getCompanyList, getUserInfoAPI, logout as logoutApi } from '@/servers/user'
import { RequestEnums } from '@/servers/fetch'
import { message } from 'antd'
import { triggerGotoLoginExpiredEvent } from '@/business/login/help'
import { $t } from '@/i18n/intl'

const cacheExpireTime = 60 * 60 * 24

export const clearLoginState = () => {
  const { userStore, agentV2Store } = getStores()
  userStore.userInfo.token = ''
  storage.remove(storagekeys.userInfo)
  userStore.updateSelectedCompanyInfo(Object.assign({}, defaultSelectedCompanyInfo))
  userStore.commitUpdateAction(s => {
    s.userInfo = defaultUserInfo
    s.companyList = []
  })
  userStore.showLoginModal = true

  agentV2Store.reset()
}

export interface CacheUserInfo {
  userInfo?: LoginRepsData
  country?: string
  selectedCompanyInfo?: CompanyListItem | undefined
  wallets?: WalletInfo[]
  credit?: CreditInfo
  currency_available?: number
  available_usd?: number
  total_balance?: number
}

/** 拉取公司列表，并选中第一家公司（可用） */
export const initCompanyData = async (): Promise<boolean> => {
  const { userStore } = getStores()
  const cache = getStorageUserInfo()

  try {
    const res = await getCompanyList()
    if (res.code !== RequestEnums.SUCCESS || !res.data?.length) {
      message.error(res.message || '获取公司列表失败')
      return false
    }

    const companyList = res.data
    userStore.commitUpdateAction(s => {
      s.companyList = companyList
    })

    const cachedId = cache?.selectedCompanyInfo?.company_ex_id
    const selected =
      (cachedId && companyList.find(item => item.company_ex_id === cachedId)) ||
      companyList.find(item => item.status !== 2) ||
      companyList[0]

    userStore.updateSelectedCompanyInfo(selected)
    updateStorageUserInfo({
      userInfo: userStore.userInfo,
      selectedCompanyInfo: selected
    })
    return true
  } catch (err) {
    console.error(err)
    message.error($t('const.server_error_tips'))
    return false
  }
}

/** 进入应用时校验登录态，并同步是否展示登录弹层 */
export const initAuthStatus = async (): Promise<boolean> => {
  const { userStore } = getStores()
  const ok = await checkLoginStatus()
  if (ok) {
    const cache = getStorageUserInfo()
    if (cache?.selectedCompanyInfo?.company_ex_id) {
      userStore.updateSelectedCompanyInfo(cache.selectedCompanyInfo)
    }
    if (!userStore.selectedCompanyInfo?.company_ex_id) {
      await initCompanyData()
    } else if (userStore.companyList.length === 0) {
      const res = await getCompanyList()
      if (res.code === RequestEnums.SUCCESS) {
        userStore.commitUpdateAction(s => {
          s.companyList = res.data
        })
      }
    }
  }
  userStore.showLoginModal = !ok
  return ok
}

/** 校验本地 token 是否有效 */
export const checkLoginStatus = async (): Promise<boolean> => {
  const cache = getStorageUserInfo()
  const token = cache?.userInfo?.token
  if (!token) {
    clearLoginState()
    return false
  }

  const { userStore } = getStores()
  if (!userStore.userInfo.token) {
    userStore.commitUpdateAction(s => {
      s.userInfo = cache.userInfo!
      if (cache.selectedCompanyInfo?.company_ex_id) {
        s.selectedCompanyInfo = cache.selectedCompanyInfo
      }
    })
  }

  try {
    const result = await getUserInfoAPI()
    if (result?.code === 0) {
      updateStorageUserInfo({ userInfo: result.data })
      userStore.commitUpdateAction(s => {
        s.userInfo = result.data
      })
      return true
    }
    if (result?.code === 9) {
      message.error($t('system.login_expired_tips'))
      clearLoginState()
      triggerGotoLoginExpiredEvent()
      return false
    }
    return false
  } catch (err) {
    console.error(err)
    return Boolean(token)
  }
}

/** 登录成功：写入用户信息、初始化公司、关闭登录弹层 */
export const processLogin = async (userInfo: LoginRepsData): Promise<boolean> => {
  const { userStore } = getStores()
  if (!userInfo?.token) {
    return false
  }
  userStore.commitUpdateAction(s => {
    s.userInfo = userInfo
  })
  updateStorageUserInfo({ userInfo })

  const companyOk = await initCompanyData()
  if (!companyOk) {
    return false
  }

  userStore.showLoginModal = false
  return true
}

export const processLogout = async () => {
  return logoutApi()
    .then(res => {
      if (res.code === RequestEnums.SUCCESS) {
        clearLoginState()
      } else {
        res.message && message.error(res.message)
      }
    })
    .catch(err => {
      console.error(err)
    })
}

export const updateStorageUserInfo = (data: CacheUserInfo): boolean => {
  const oldData = getStorageUserInfo()
  storage.set(
    storagekeys.userInfo,
    {
      ...oldData,
      ...data
    },
    cacheExpireTime
  )
  return true
}

export const getStorageUserInfo = (): CacheUserInfo | null => {
  const data = storage.get<CacheUserInfo>(storagekeys.userInfo)
  return data ? data : null
}

export const removeStorageUserInfo = () => {
  storage.remove(storagekeys.userInfo)
}
